<template>
    <div
    class="generic-container"
    :class="{'mobile': getMobileAppState, 'is-open': isOpen, 'error': !isOpen && hasError}">
        <div class="header" @click="headerClickHandler">
            <chevron-down-icon
            class="icon"
            :class="{'is-open': isOpen}"/>
            <!-- Title -->
            <div class="title" >{{ title }}</div>
            <!-- Dropdown Options -->
            <dropdown-more-options
            v-if="options && options.length > 1"
            buttonSize="tiny"
            :options="options"
            @click.stop
            @clicked="emit('moreOptionsClicked', $event)"/>
            <!-- Solo button if only one option is provided -->
			<button-quaternary
            v-else-if="options"
            buttonSize="tiny"
            :icon="options[0].icon"
            @clicked="emit('moreOptionsClicked', options[0].id)"/>
        </div>

        <transition
        name="show-details"
        @after-enter="isVisible = true">
            <div
            v-show="isOpen"
            class="content"
            :class="{'is-visible': isVisible}">
                <slot></slot>
            </div>
        </transition>
    </div>
</template>

<script setup>
import ChevronDownIcon from '@/assets/icons/arrow-down.svg?inline'
import DropdownMoreOptions from '@/components/generic/dropdowns/moreOptions/MoreOptions'
import ButtonQuaternary from '@/components/generic/buttons/Quaternary'
import { ref, watch } from 'vue'

/* Emmitters */
const emit = defineEmits(['moreOptionsClicked', 'headerClicked'])

const props = defineProps({
    options: Array,
    title: String,
    hasError: Boolean
})

const isOpen = ref(true)
const isVisible = ref(true)

const headerClickHandler = () => {
    isOpen.value = !isOpen.value
    isVisible.value = false

    emit('headerClicked', isOpen.value)
}

</script>

<style lang="scss" scoped>
.generic-container {
    width: 100%;
    height: auto;

    display: flex;
    flex-direction: column;

    padding: 0 16px;
    border-radius: 8px;
    border: solid 1px var(--secondary);

    transition: all 800ms ease-in;

    &.is-open {
        padding-bottom: 16px;
    }

    &.error {
        border: solid 1px var(--negative);
    }

    .header {
        width: 100%;
        min-height: 40px;
        padding: 16px 0;

        display: flex;
        gap: 8px;
        cursor: pointer;

        svg {
            transition: all 500ms ease-out;
            margin-top: 2px;

            :deep(.change-color-fill) {
                fill: var(--secondary-dark-2);
            }

            &.is-open {
                transform: rotate(180deg);
            }
        }

        .title {
            width: 100%;

            @include H5-Medium;
            line-height: 20px;
            color: var(--secondary-dark-3);
        }
    }

    .content {
        width: 100%;
        height: auto;
        overflow: hidden;

        display: flex;
        flex-direction: column;
        gap: 24px;

        &.is-visible {
            overflow: visible;
        }

        &.show-details-enter-active,
        &.show-details-leave-active {
            max-height: 1000px;
            opacity: 1;
            transition: all 800ms ease-in;
        }

        &.show-details-enter-from,
        &.show-details-leave-to {
            max-height: 0;
            opacity: 0.2;
            transition: all 500ms ease-out;
        }
    }
}
</style>
